/* eslint-disable no-unused-vars */
import {
  DeliveryMethod,
  VerificationAction,
} from 'domains/Users/Auth/store/Auth/interface'
import navConfig from '~/layouts/dashboard/navbar/NavConfig'
import { DataResponse } from '~/@types/commons'
import { UserProfile } from '~/@types/profile'
import { AxiosResponse } from 'axios'
import type { GuestOtpRunner } from 'hooks/domains/Users/useGuestOtpChallenge'

export enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  LoginPhone = 'LOGIN_PHONE',
  LoginSocial = 'LOGIN_SOCIAL',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
  Authenticated = 'AUTHENTICATED',
}

export type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key
      }
    : {
        type: Key
        payload: M[Key]
      }
}

export type AuthUser = null | Record<string, any>

export type AuthState = {
  isAuthenticated: boolean
  isInitialized: boolean
  user: AuthUser
  isOtp: boolean
}

export type NavConfigType = typeof navConfig

export type AuthContextType = {
  isAuthenticated: boolean
  isInitialized: boolean
  isOtp: boolean
  user: AuthUser
  loginPhone: (
    phone: string,
    method: DeliveryMethod,
    runGuestOtp: GuestOtpRunner,
    action?: string,
  ) => Promise<void>
  // Team-invite accept flow: resolves the phone from an invitation_token
  // (URL param from the email/WhatsApp invite link) instead of a user-typed
  // number; the phone to display/verify comes back on `user.phoneNumber`.
  loginPhoneByInvitation: (
    invitationToken: string,
    method: DeliveryMethod,
    runGuestOtp: GuestOtpRunner,
  ) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  loginSocial: (provider: string) => Promise<void>
  verify: (
    otp: string,
    delivery_method: DeliveryMethod,
    username: string,
    action?: string,
    invitationToken?: string,
  ) => Promise<void>
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) => Promise<void>
  logout: () => Promise<void>
  claimToken: (tokenId: string) => Promise<void>
  targetRedirectUrl: string
  getUserProfile: () => Promise<DataResponse<UserProfile>>
  getUserKYCTier3: () => Promise<void>
  userKYCTier3: () => Promise<void>
  sendOTP: (
    phone: string,
    method: DeliveryMethod,
    action?: VerificationAction,
  ) => Promise<AxiosResponse>
  doUserAuthenticated: () => Promise<void>
  // Session scope + capabilities from GET /me, fetched once by AuthProvider.
  // Read these through hooks/commons/useSession rather than useAuth — it adds
  // the multiple-roles flag awareness that AuthProvider can't (it sits outside
  // FlagProvider).
  orgId: string
  orgName: string
  // The user's role in the active org, e.g. 'owner'. Distinct from `roles`,
  // which is the legacy creator-account feature whitelist.
  orgRole: string
  permissions: string[]
  isLoadingPermissions: boolean
}
